/**
 * Trading Controls Component
 * Control automated trading with safety features
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from './card'
import { Badge, StatusBadge } from './badge'
import { Play, Pause, Square, AlertTriangle, Shield, Power, Zap } from 'lucide-react'

interface TradingControlsProps {
  isRunning: boolean
  isPaused: boolean
  onStart: () => void
  onPause: () => void
  onStop: () => void
  onEmergencyStop: () => void
}

export function TradingControls({
  isRunning,
  isPaused,
  onStart,
  onPause,
  onStop,
  onEmergencyStop,
}: TradingControlsProps) {
  const [emergencyConfirm, setEmergencyConfirm] = useState(false)

  const handleEmergencyStop = () => {
    if (emergencyConfirm) {
      onEmergencyStop()
      setEmergencyConfirm(false)
    } else {
      setEmergencyConfirm(true)
      setTimeout(() => setEmergencyConfirm(false), 3000)
    }
  }

  return (
    <Card className={isRunning ? 'border-success/30' : 'border-border'}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Power className="h-5 w-5" />
          Trading Controls
          {isRunning && (
            <Badge variant="success" size="sm">
              <div className="h-2 w-2 rounded-full bg-success-foreground animate-pulse mr-1" />
              Active
            </Badge>
          )}
          {isPaused && (
            <Badge variant="warning" size="sm">
              Paused
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main Controls */}
        <div className="grid grid-cols-3 gap-3">
          {!isRunning ? (
            <Button onClick={onStart} className="w-full" size="lg" variant="default">
              <Play className="h-5 w-5 mr-2" />
              Start Trading
            </Button>
          ) : (
            <>
              <Button
                onClick={onPause}
                className="w-full"
                size="lg"
                variant={isPaused ? 'default' : 'outline'}
              >
                {isPaused ? (
                  <>
                    <Play className="h-5 w-5 mr-2" />
                    Resume
                  </>
                ) : (
                  <>
                    <Pause className="h-5 w-5 mr-2" />
                    Pause
                  </>
                )}
              </Button>
              <Button onClick={onStop} className="w-full" size="lg" variant="outline">
                <Square className="h-5 w-5 mr-2" />
                Stop
              </Button>
            </>
          )}
        </div>

        {/* Emergency Stop */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <span className="text-sm font-medium text-destructive">Emergency Controls</span>
            </div>
          </div>
          <Button
            onClick={handleEmergencyStop}
            className="w-full"
            size="lg"
            variant={emergencyConfirm ? 'destructive' : 'outline'}
          >
            <Zap className="h-5 w-5 mr-2" />
            {emergencyConfirm ? 'CONFIRM EMERGENCY STOP' : 'Emergency Stop All Trading'}
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            Immediately closes all positions and stops all algorithms. Use only in emergencies.
          </p>
        </div>

        {/* Safety Status */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="h-4 w-4 text-success" />
            <span className="text-sm font-medium">Safety Systems</span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Daily Loss Limit</span>
              <Badge variant="success">Active</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Max Drawdown Protection</span>
              <Badge variant="success">Active</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Position Size Limits</span>
              <Badge variant="success">Active</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Emergency Stop</span>
              <Badge variant="success">Armed</Badge>
            </div>
          </div>
        </div>

        {/* Current Session Stats */}
        {isRunning && (
          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-medium mb-3">Current Session</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Duration: </span>
                <span className="font-medium">2h 34m</span>
              </div>
              <div>
                <span className="text-muted-foreground">Trades: </span>
                <span className="font-medium">12</span>
              </div>
              <div>
                <span className="text-muted-foreground">P&L: </span>
                <span className="font-medium text-success">+$145.00</span>
              </div>
              <div>
                <span className="text-muted-foreground">Return: </span>
                <span className="font-medium text-success">+1.45%</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
